import { useState, useCallback, useRef, type ElementType } from "react";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { UserCard } from "@/components/admin/UserCard";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type AdminTab = "users" | "data" | "emailer" | "domains" | "audit" | "debug";

const ADMIN_TABS: { value: AdminTab; label: string; icon: ElementType }[] = [
  { value: "users", label: "Users", icon: Users },
  { value: "data", label: "Data", icon: Database },
  { value: "emailer", label: "Emailer", icon: Mail },
  { value: "domains", label: "Allowed Domains", icon: Globe },
  { value: "audit", label: "Audit Logs", icon: FileText },
  { value: "debug", label: "Debug", icon: Bug },
];
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Trash2, AlertCircle, Pencil, Users, Database, Globe, FileText, Bug, Info, UserPlus, Filter, UserCog, Mail } from "lucide-react";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import { EditUserDialog } from "@/components/admin/EditUserDialog";
import { UserQualificationsSheet } from "@/components/admin/UserQualificationsSheet";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { ChipFilter, ChipFilterOption } from "@/components/ui/chip-filter";
import { AddFilterMenu, FilterCategory } from "@/components/ui/add-filter-menu";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useUserRole } from "@/hooks/useUserRole";
import { useTenant } from "@/hooks/useTenant";


import { AdminDataTab } from "@/components/admin/AdminDataTab";
import { AllowedDomainsTab } from "@/components/admin/AllowedDomainsTab";
import { AuditLogsTab } from "@/components/admin/AuditLogsTab";
import { EmailerTab } from "@/components/admin/EmailerTab";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SamsaraDebugTab } from "@/components/admin/SamsaraDebugTab";
import { S3MetadataOutboxHealth } from "@/components/admin/S3MetadataOutboxHealth";

type AppRole = "admin" | "driver" | "dispatcher" | "service_technician" | "terminal_staff" | "analyst";

interface TenantUser {
  user_id: string;
  email: string;
  full_name: string;
  effective_roles: AppRole[];
  global_roles: AppRole[];
  tenant_only_roles: AppRole[];
}

const ROLE_LABELS: Record<AppRole, string> = {
  admin: "Admin",
  driver: "Driver",
  dispatcher: "Dispatcher",
  service_technician: "Service Technician",
  terminal_staff: "Terminal Staff",
  analyst: "Analyst",
};


const ALL_ROLES: AppRole[] = ["admin", "dispatcher", "driver", "service_technician", "terminal_staff", "analyst"];
const ASSIGNABLE_ROLES: AppRole[] = ALL_ROLES.filter((r) => r !== "driver");

function getRoleScope(role: AppRole, user: TenantUser, tenantName: string): string {
  // If the role is in global_roles, it applies everywhere
  if (user.global_roles.includes(role)) return "All environments";
  // If the role is tenant-only, it's scoped
  if (user.tenant_only_roles.includes(role)) return `${tenantName} only`;
  return "All environments";
}



export default function Admin() {
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const { tenantId, activeTenant, tenants } = useTenant();
  const [editDialogUser, setEditDialogUser] = useState<TenantUser | null>(null);
  const [qualUser, setQualUser] = useState<TenantUser | null>(null);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRoleEntries, setNewUserRoleEntries] = useState<{ role: AppRole; scope: "all" | "tenant" }[]>([]);
  const [activeTab, setActiveTab] = useState<AdminTab>("users");
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string[]>(["all"]);
  const [activeUserFilters, setActiveUserFilters] = useState<string[]>([]);
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const ignoreAutoCloseRef = useRef<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "user" | "override"; userId: string; email: string; role?: AppRole } | null>(null);
  

  const currentTenantName = tenants.find(t => t.slug === activeTenant)?.name ?? activeTenant;
  const isProd = activeTenant === "prod";

  // Fetch users with effective roles for current tenant
  const { data: tenantUsers, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ["users-for-tenant", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_users_for_tenant" as any, { _tenant_id: tenantId });
      if (error) throw error;
      return (data as TenantUser[]).map(u => ({
        ...u,
        effective_roles: u.effective_roles ?? [],
        global_roles: u.global_roles ?? [],
        tenant_only_roles: u.tenant_only_roles ?? [],
      }));
    },
    enabled: !!tenantId,
  });


  const createUserMutation = useMutation({
    mutationFn: async ({ name, email, roles }: { name: string; email: string; roles: AppRole[] }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      const response = await supabase.functions.invoke("create-user", {
        body: { name, email, roles },
      });
      if (response.error) throw new Error(response.error.message);
      if (response.data?.error) throw new Error(response.data.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-for-tenant", tenantId] });
      toast.success("User created successfully");
      setIsAddUserOpen(false);
      setNewUserName("");
      setNewUserEmail("");
      setNewUserRoleEntries([]);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const removeRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AppRole }) => {
      const { error } = await supabase.rpc("remove_tenant_user_role" as any, {
        _target_user_id: userId,
        _tenant_id: tenantId,
        _role: role,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-for-tenant", tenantId] });
      toast.success("Role override removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleRoleMutation = useMutation({
    mutationFn: async ({ userId, role, add }: { userId: string; role: AppRole; add: boolean }) => {
      if (add) {
        const { error } = await supabase.rpc("upsert_tenant_user_role" as any, {
          _target_user_id: userId,
          _tenant_id: tenantId,
          _role: role,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc("remove_tenant_user_role" as any, {
          _target_user_id: userId,
          _tenant_id: tenantId,
          _role: role,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-for-tenant", tenantId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      const response = await supabase.functions.invoke("delete-user", {
        body: { user_id: userId },
      });
      if (response.error) throw new Error(response.error.message);
      if (response.data?.error) throw new Error(response.data.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-for-tenant", tenantId] });
      toast.success("User deleted successfully");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Map tenantUsers to have `id` for useBulkSelection
  const usersWithId = tenantUsers?.map(u => ({ ...u, id: u.user_id }));
  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count: bulkCount } = useBulkSelection(usersWithId);

  const filteredUsers = (tenantUsers ?? []).filter((u) => {
    const matchesSearch = !userSearch || (() => {
      const q = userSearch.toLowerCase();
      return u.email.toLowerCase().includes(q) || (u.full_name && u.full_name.toLowerCase().includes(q));
    })();
    const matchesRole = roleFilter.includes("all") || u.effective_roles.some((r) => roleFilter.includes(r));
    return matchesSearch && matchesRole;
  });


  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageRows: pagedUsers,
  } = useTablePagination(filteredUsers);

  const bulkDeleteMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      for (const userId of userIds) {
        const response = await supabase.functions.invoke("delete-user", {
          body: { user_id: userId },
        });
        if (response.error) throw new Error(response.error.message);
        if (response.data?.error) throw new Error(response.data.error);
      }
      return userIds.length;
    },
    onSuccess: (deletedCount) => {
      queryClient.invalidateQueries({ queryKey: ["users-for-tenant", tenantId] });
      toast.success(`Deleted ${deletedCount} user(s)`);
      clear();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Redirect non-admin users
  if (!roleLoading && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppLayout
      title="Admin"
      subtitle={
        <div className="flex items-center gap-6">
          {ADMIN_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                "flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === tab.value
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      }
    >
      <div className="space-y-6">

        {usersError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load user data: {usersError.message}
            </AlertDescription>
          </Alert>
        )}

        {activeTab === "users" && (
          <>
            <Alert className={isProd ? "border-destructive/50 bg-destructive/5 mb-4" : "border-primary/30 bg-primary/5 mb-4"}>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {isProd
                  ? <>You are managing <strong>Production</strong> roles. Changes made here will impact access to the <strong>Production</strong> environment.</>
                  : <>You are managing roles for <strong>{currentTenantName}</strong>. Changes here only affect this environment.</>
                }
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Users
                  </CardTitle>
                  <CardDescription>
                    Manage user roles and access. To add a driver, go to the{" "}
                    <a href="/drivers" className="text-primary underline hover:no-underline">Drivers page</a>.
                  </CardDescription>
                </div>
                {isProd && (
                  <Button onClick={() => setIsAddUserOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    Loading users...
                  </div>
                ) : !tenantUsers || tenantUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">No users found.</p>
                ) : (
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        <SearchChip query={userSearch} onClear={() => setUserSearch("")} />
                        {activeUserFilters.includes("role") && (
                          <ChipFilter
                            label="Role"
                            icon={<UserCog className="h-3.5 w-3.5" />}
                            value={roleFilter}
                            onChange={(v) => setRoleFilter(Array.isArray(v) ? v : [v])}
                            onRemove={() => {
                              setActiveUserFilters((prev) => prev.filter((k) => k !== "role"));
                              setRoleFilter(["all"]);
                              setOpenFilterKey((prev) => (prev === "role" ? null : prev));
                            }}
                            multiSelect
                            options={ALL_ROLES.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
                            open={openFilterKey === "role"}
                            onOpenChange={(isOpen) => {
                              if (!isOpen && ignoreAutoCloseRef.current === "role") {
                                ignoreAutoCloseRef.current = null;
                                return;
                              }
                              if (!isOpen) {
                                if (roleFilter.includes("all")) {
                                  setActiveUserFilters((prev) => prev.filter((k) => k !== "role"));
                                }
                                setOpenFilterKey((prev) => (prev === "role" ? null : prev));
                              } else {
                                setOpenFilterKey("role");
                              }
                            }}
                          />
                        )}
                        {!activeUserFilters.includes("role") && (
                          <AddFilterMenu
                            categories={[{ key: "role", label: "Role", icon: <UserCog className="h-3.5 w-3.5" /> }]}
                            onSelect={(key) => {
                              setActiveUserFilters((prev) => [...prev, key]);
                              window.setTimeout(() => {
                                ignoreAutoCloseRef.current = key;
                                setOpenFilterKey(key);
                                window.setTimeout(() => {
                                  if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
                                }, 250);
                              }, 50);
                            }}
                          />
                        )}
                        {activeUserFilters.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sm text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setActiveUserFilters([]);
                              setOpenFilterKey(null);
                              ignoreAutoCloseRef.current = null;
                              setRoleFilter(["all"]);
                            }}
                          >
                            Reset filters
                          </Button>
                        )}
                      </div>
                      <div className="ml-auto flex items-center gap-3">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">Showing {filteredUsers.length} user{filteredUsers.length === 1 ? "" : "s"}</span>
                        <ExpandingSearch value={userSearch} onChange={setUserSearch} placeholder="Search users..." />
                      </div>
                    </div>
                    <BulkActionBar count={bulkCount} onClear={clear} entityLabel="user">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </BulkActionBar>
                    <div className="rounded-lg border bg-card">
                      <ResponsiveTableShell
                        cards={
                          <div className="flex flex-col gap-3 p-3">
                            {pagedUsers.length === 0 ? (
                              <div className="text-center py-8 text-muted-foreground">
                                {(userSearch.trim() || !roleFilter.includes("all")) ? "No users match your filter criteria" : "No users found"}
                              </div>
                            ) : (
                              pagedUsers.map((user) => (
                                <UserCard
                                  key={user.user_id}
                                  fullName={user.full_name}
                                  email={user.email}
                                  roleLabels={user.effective_roles.map((r) => ROLE_LABELS[r])}
                                  isChecked={selectedIds.has(user.user_id)}
                                  onClick={() => setQualUser(user)}
                                  onToggle={() => toggle(user.user_id)}
                                  onEdit={() => setEditDialogUser(user)}
                                  onDelete={() => deleteUserMutation.mutate(user.user_id)}
                                  isDeletePending={deleteUserMutation.isPending}
                                />
                              ))
                            )}
                          </div>
                        }
                        table={
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[40px]">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={toggleAll}
                                aria-label="Select all"
                                {...(someSelected ? { "data-state": "indeterminate" } : {})}
                              />
                            </TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Roles</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pagedUsers.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                {(userSearch.trim() || !roleFilter.includes("all")) ? "No users match your filter criteria" : "No users found"}
                              </TableCell>
                            </TableRow>
                          ) : pagedUsers.map((user) => (
                            <TableRow
                              key={user.user_id}
                              className="cursor-pointer"
                              onClick={() => setQualUser(user)}
                            >
                              <TableCell className="w-[40px]" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedIds.has(user.user_id)}
                                  onCheckedChange={() => toggle(user.user_id)}
                                  aria-label={`Select ${user.email}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium whitespace-nowrap">
                                {user.full_name || "—"}
                              </TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {user.effective_roles.map((r) => (
                                    <Badge key={r} variant="secondary">
                                      {ROLE_LABELS[r]}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditDialogUser(user)}
                                    title="Edit user"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <ConfirmDeleteButton
                                    onConfirm={() => deleteUserMutation.mutate(user.user_id)}
                                    entityName="user"
                                    entityLabel={user.email}
                                    isPending={deleteUserMutation.isPending}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                        }
                      />
                      <TablePagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}


        

        {activeTab === "data" && <AdminDataTab />}

        {activeTab === "emailer" && <EmailerTab />}

        {activeTab === "domains" && <AllowedDomainsTab />}

        {activeTab === "audit" && <AuditLogsTab />}

        {activeTab === "debug" && (
          <div className="space-y-6">
            <S3MetadataOutboxHealth />
            <SamsaraDebugTab />
          </div>
        )}

        <EditUserDialog
          user={editDialogUser}
          open={!!editDialogUser}
          onOpenChange={(open) => !open && setEditDialogUser(null)}
          tenantId={tenantId}
          tenantName={currentTenantName}
          isProd={isProd}
        />

        <UserQualificationsSheet
          user={
            qualUser
              ? {
                  email: qualUser.email,
                  full_name: qualUser.full_name,
                  roleLabels: qualUser.effective_roles.map((r) => ROLE_LABELS[r]),
                }
              : null
          }
          open={!!qualUser}
          onOpenChange={(open) => !open && setQualUser(null)}
        />

        {/* Confirmation dialog for destructive actions */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {deleteConfirm?.type === "user" ? "Delete User" : "Remove Role Override"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteConfirm?.type === "user"
                  ? `Are you sure you want to permanently delete ${deleteConfirm?.email}? This action cannot be undone.`
                  : `Are you sure you want to remove the ${deleteConfirm?.role ? ROLE_LABELS[deleteConfirm.role] + " " : ""}role override for ${deleteConfirm?.email}? They will revert to their global role for this environment.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (!deleteConfirm) return;
                  if (deleteConfirm.type === "user") {
                    deleteUserMutation.mutate(deleteConfirm.userId);
                  } else if (deleteConfirm.role) {
                    removeRoleMutation.mutate({ userId: deleteConfirm.userId, role: deleteConfirm.role });
                  }
                  setDeleteConfirm(null);
                }}
              >
                {deleteConfirm?.type === "user" ? "Delete User" : "Remove Override"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="new-user-name">Name</Label>
                <Input
                  id="new-user-name"
                  placeholder="Full name"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-user-email">Email</Label>
                <Input
                  id="new-user-email"
                  type="email"
                  placeholder="user@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>
              <div className="space-y-3">
                <Label>Roles</Label>
                <div className="space-y-2">
                  {ASSIGNABLE_ROLES.map((role) => {
                     const isActive = newUserRoleEntries.some((e) => e.role === role);
                     const entry = newUserRoleEntries.find((e) => e.role === role);
                    return (
                      <div key={role} className="flex items-center justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm cursor-pointer flex-1">
                          <Checkbox
                            checked={isActive}
                            onCheckedChange={(checked) => {
                              setNewUserRoleEntries((prev) =>
                                checked
                                  ? [...prev, { role, scope: "all" }]
                                  : prev.filter((e) => e.role !== role)
                              );
                            }}
                          />
                          {ROLE_LABELS[role]}
                        </label>
                        {isActive && (
                          <Select
                            value={entry?.scope ?? "all"}
                            onValueChange={(v) =>
                              setNewUserRoleEntries((prev) =>
                                prev.map((e) => (e.role === role ? { ...e, scope: v as "all" | "tenant" } : e))
                              )
                            }
                          >
                            <SelectTrigger className="h-8 w-[160px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All environments</SelectItem>
                              <SelectItem value="tenant">{currentTenantName} only</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsAddUserOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (newUserEmail && newUserRoleEntries.length > 0) {
                      createUserMutation.mutate({ name: newUserName, email: newUserEmail, roles: newUserRoleEntries.map((e) => e.role) });
                    }
                  }}
                  disabled={!newUserEmail || newUserRoleEntries.length === 0 || createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? "Adding..." : "Add User"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
